import * as React from "react";
import { X } from "lucide-react";
import { cn } from "@lib/utils";

interface MultiSelectProps {
  options: string[];
  selected: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export function MultiSelect({
  options,
  selected,
  onChange,
  placeholder = "Select options...",
  disabled = false,
  className,
}: MultiSelectProps) {
  const [isOpen, setIsOpen] = React.useState(false);

  const handleSelect = (option: string) => {
    if (!selected.includes(option)) {
      onChange([...selected, option]);
    }
    setIsOpen(false);
  };

  const handleRemove = (option: string) => {
    onChange(selected.filter((item) => item !== option));
  };

  return (
    <div className={cn("relative w-full", className)}>
      {/* Clickable box */}
      <div
        className={cn(
          "border rounded-lg px-3 py-2 min-h-[48px] flex flex-wrap gap-2 items-center cursor-pointer transition-all bg-white",
          disabled && "bg-gray-100 cursor-not-allowed"
        )}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        {selected.length > 0 ? (
          selected.map((item) => (
            <div
              key={item}
              className="bg-gray-200 text-gray-800 px-3 py-1 rounded-full flex items-center text-sm max-w-full"
            >
              <span className="truncate">{item}</span>
              {!disabled && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemove(item);
                  }}
                  className="ml-1 text-gray-500 hover:text-gray-700"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          ))
        ) : (
          <div className="text-gray-400">{placeholder}</div>
        )}
      </div>

      {/* Dropdown */}
      {isOpen && !disabled && (
        <div className="absolute z-10 mt-1 w-full bg-white border rounded-lg shadow-lg max-h-60 overflow-auto">
          {options.filter((option) => !selected.includes(option)).length > 0 ? (
            options
              .filter((option) => !selected.includes(option))
              .map((option) => (
                <div
                  key={option}
                  className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                  onClick={() => handleSelect(option)}
                >
                  {option}
                </div>
              ))
          ) : (
            <div className="px-3 py-2 text-gray-500">All options selected</div>
          )}
        </div>
      )}
    </div>
  );
}
